import { OrderDirection } from '@griply/types';
import { buildSortedGoalTree, getOrderedGoalIds } from '@griply/utils';
import { useGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useGoalsOverviewFilterDocument } from 'features/user';
import { useMemo } from 'react';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useUser } from 'shared/hooks/use-user';

// The canonical goal order for both the list and the timeline. Built from the
// goals-overview *filter document* (not the legacy `goalGroupBy`/`goalSortingMode`
// that `useSortedGoals` reads) so the grouping that decides which custom-order
// store applies — `goalRoadmapSorting` for None, `lifeArea.goalSorting` for
// Life area — matches what the views actually render.
export const useGoalsOverviewOrder = () => {
  const user = useUser();
  const goals = useGoals();
  const sortedLifeAreas = useSortedLocalizedLifeAreas();
  const filterDocument = useGoalsOverviewFilterDocument();
  const taskCounts = useTaskCounts();
  const sortDirection = user?.settings?.goalSortDirection ?? OrderDirection.Asc;
  const goalRoadmapSorting = user?.settings?.goalRoadmapSorting;
  const goalGroupSorting = user?.settings?.goalGroupSorting;

  return useMemo(() => {
    const sorted = buildSortedGoalTree({
      goals,
      sortedLifeAreas,
      goalGroupBy: filterDocument.groupByMode ?? undefined,
      goalSortingMode: filterDocument.sortingMode ?? undefined,
      goalRoadmapSorting,
      goalGroupSorting,
      taskCounts: taskCounts?.byGoalId,
      goalProgressCounts: taskCounts?.byGoalProgress,
    });

    return getOrderedGoalIds(
      sorted,
      filterDocument.sortingMode ?? undefined,
      sortDirection,
    );
  }, [
    goals,
    sortedLifeAreas,
    filterDocument.groupByMode,
    filterDocument.sortingMode,
    sortDirection,
    goalRoadmapSorting,
    goalGroupSorting,
    taskCounts?.byGoalId,
    taskCounts?.byGoalProgress,
  ]);
};
